import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "./firebase";
import { createDailyRoom, deleteDailyRoom } from "./calls";

export const DAYS_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
export const DAYS_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// ── Config ──────────────────────────────────────────────────────────────────

export async function getRecurringCallConfig() {
  const snap = await getDoc(doc(db, "appConfig", "recurringCall"));
  return snap.exists() ? snap.data() : null;
}

export async function setRecurringCallConfig(config) {
  await setDoc(doc(db, "appConfig", "recurringCall"), {
    ...config,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function subscribeToRecurringConfig(callback) {
  return onSnapshot(
    doc(db, "appConfig", "recurringCall"),
    (snap) => callback(snap.exists() ? snap.data() : null),
    () => callback(null)
  );
}

// ── Per-occurrence (callPolls) ──────────────────────────────────────────────

export async function getCallPoll(dateKey) {
  const snap = await getDoc(doc(db, "callPolls", dateKey));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function subscribeToCallPoll(dateKey, callback) {
  if (!dateKey) { callback(null); return () => {}; }
  return onSnapshot(
    doc(db, "callPolls", dateKey),
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    () => callback(null)
  );
}

export async function getCallPollsForDateKeys(dateKeys) {
  const snaps = await Promise.all(dateKeys.map((k) => getDoc(doc(db, "callPolls", k))));
  const map = {};
  snaps.forEach((snap, i) => {
    map[dateKeys[i]] = snap.exists() ? { id: snap.id, ...snap.data() } : null;
  });
  return map;
}

// Get any exception occurrences (rescheduled/added non-recurring dates) upcoming
export async function getUpcomingExceptions(from, to) {
  const snap = await getDocs(
    query(collection(db, "callPolls"), where("isException", "==", true))
  );
  const fromKey = dateKeyFromDate(from);
  const toKey = dateKeyFromDate(to);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.date >= fromKey && p.date <= toKey && p.status !== "cancelled");
}

// ── RSVP ────────────────────────────────────────────────────────────────────

export async function setRSVP(dateKey, uid, response) {
  const ref = doc(db, "callPolls", dateKey);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { [`rsvps.${uid}`]: response });
  } else {
    await setDoc(ref, { date: dateKey, rsvps: { [uid]: response }, status: "upcoming" });
  }
}

// ── Cancel / Reschedule ────────────────────────────────────────────────────

export async function cancelOccurrence(dateKey, note) {
  await setDoc(doc(db, "callPolls", dateKey), {
    date: dateKey,
    status: "cancelled",
    note: note || "",
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function rescheduleOccurrence(originalDateKey, newDateKey, newScheduledTime, note) {
  // Mark original as cancelled
  await setDoc(doc(db, "callPolls", originalDateKey), {
    date: originalDateKey,
    status: "cancelled",
    rescheduledToDate: newDateKey,
    note: note || "",
    updatedAt: serverTimestamp(),
  }, { merge: true });

  // Create the replacement occurrence
  await setDoc(doc(db, "callPolls", newDateKey), {
    date: newDateKey,
    scheduledTime: Timestamp.fromDate(new Date(newScheduledTime)),
    status: "upcoming",
    isException: true,
    replacesDate: originalDateKey,
    note: note || "",
    rsvps: {},
    updatedAt: serverTimestamp(),
  });
}

// ── Room management ─────────────────────────────────────────────────────────

export async function createRoomForOccurrence(dateKey, scheduledTime) {
  const { roomUrl, roomName } = await createDailyRoom(`poll-${dateKey}`, scheduledTime);
  await setDoc(doc(db, "callPolls", dateKey), {
    date: dateKey,
    roomUrl,
    roomName,
    status: "upcoming",
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return { roomUrl, roomName };
}

export async function removeRoomForOccurrence(dateKey, roomName) {
  await deleteDailyRoom(roomName);
  await setDoc(doc(db, "callPolls", dateKey), {
    roomUrl: null,
    roomName: null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ── Compute upcoming dates ──────────────────────────────────────────────────

export function computeEffectiveEndDate(config) {
  const { endDate, startDate, totalCalls, dayOfWeek, hour = 19, minute = 0 } = config || {};
  if (endDate) return new Date(`${endDate}T23:59:59`);
  if (totalCalls && startDate) {
    const configStart = new Date(`${startDate}T00:00:00`);
    const startOffset = (dayOfWeek - configStart.getDay() + 7) % 7;
    const first = new Date(configStart);
    first.setDate(configStart.getDate() + startOffset);
    first.setHours(hour, minute, 0, 0);
    const last = new Date(first);
    last.setDate(first.getDate() + (totalCalls - 1) * 7);
    return last;
  }
  return null;
}

export function computeNextOccurrences(config, fromDate, n = 4) {
  if (!config?.active) return [];
  const { dayOfWeek, hour = 19, minute = 0, startDate } = config;

  // Respect startDate — don't show occurrences before it
  let effectiveFrom = fromDate;
  if (startDate) {
    const configStart = new Date(`${startDate}T00:00:00`);
    if (configStart > fromDate) effectiveFrom = configStart;
  }

  const endLimit = computeEffectiveEndDate(config);

  const start = new Date(effectiveFrom);
  start.setHours(0, 0, 0, 0);

  let offset = (dayOfWeek - start.getDay() + 7) % 7;
  if (offset === 0) {
    const todaySlot = new Date(start);
    todaySlot.setHours(hour, minute, 0, 0);
    if (todaySlot < effectiveFrom) offset = 7;
  }

  const first = new Date(start);
  first.setDate(start.getDate() + offset);
  first.setHours(hour, minute, 0, 0);

  const results = [];
  for (let i = 0; results.length < n; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() + i * 7);
    if (endLimit && d > endLimit) break;
    results.push(d);
  }
  return results;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function dateKeyFromDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function getOccurrenceStatus(scheduledTime) {
  if (!scheduledTime) return "upcoming";
  const t = scheduledTime?.toDate ? scheduledTime.toDate() : new Date(scheduledTime);
  const now = new Date();
  const end = new Date(t.getTime() + 3 * 60 * 60 * 1000);
  const fiveBefore = new Date(t.getTime() - 5 * 60 * 1000);
  if (now >= fiveBefore && now <= end) return "live";
  if (now > end) return "ended";
  return "upcoming";
}

export function formatOccurrenceTime(scheduledTime) {
  const t = scheduledTime?.toDate ? scheduledTime.toDate() : new Date(scheduledTime);
  return t.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) +
    " at " + t.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
